import { convertSecToMs } from '@packages/shared/core/common/utils/timer';
import { joinUrls } from '@packages/shared/core/common/utils/url';
import { HttpClient } from '@packages/shared/core/common/services';
import { ApiPathAskGateway } from '@packages/shared/core/common/constants/apiPaths';
import { FeatureFlags } from '@packages/shared/core/feature-flags/services';
import { expertsParamsDefaultValue, expertsParamsFeature } from '@packages/shared/core/feature-flags/config';
import { FlowName } from '@packages/shared/core/quiz/entities';
import { ExpertService } from '../interfaces';
import { mapExpertDtoToEntity, mapReviewDtoToEntity } from '../mappers';
import {
  Expert,
  ExpertId,
  ExpertReview,
  ExpertStatus,
  ExpertsRequestParams,
  GetAllReviewsParams,
  GetExpertReviewsParams,
} from '../entities';
import { getMinimalRatingQueryParam } from '../utils';
import type { ExpertByIdDataDto, ExpertDto, ExpertReviewsDataDto, ExpertsListDataDto } from './dto';

export class HttpExpertService implements ExpertService {
  constructor(
    private readonly askGatewayHttpClient: HttpClient,
    private readonly featureFlags: FeatureFlags,
  ) {}

  async getExperts(params: ExpertsRequestParams = {}): Promise<Array<Expert>> {
    const { perPage, page, customerSource, filter } = params;

    const featureExpertsParamsValue = this.featureFlags.getFeatureValue(
      expertsParamsFeature,
      expertsParamsDefaultValue,
    );

    const headers = customerSource && {
      'customer-source': `quiz_${customerSource}`,
    };

    const { data } = await this.askGatewayHttpClient.get<ExpertsListDataDto>(
      ApiPathAskGateway.ASTRO_CRM_ASTROLOGERS_OPEN,
      {
        params: {
          per_page: perPage,
          page: page,
          filter: {
            ...filter,
            ...featureExpertsParamsValue.filter,
          },
          sort: featureExpertsParamsValue.sort,
        },
        headers,
      },
    );

    return data.astrologers.map((expertDto: ExpertDto) => mapExpertDtoToEntity(expertDto));
  }

  async getExpertById(expertId: ExpertId): Promise<Expert> {
    const { data } = await this.askGatewayHttpClient.get<ExpertByIdDataDto>(
      joinUrls(ApiPathAskGateway.ASTRO_CRM_ASTROLOGERS_OPEN, expertId),
    );

    return mapExpertDtoToEntity(data.astrologer);
  }

  async getRandomExpert({ customerSource, ...params }: ExpertsRequestParams = {}): Promise<Expert> {
    const headers = customerSource && {
      'customer-source': `quiz_${customerSource}`,
    };

    const { data } = await this.askGatewayHttpClient.get<ExpertsListDataDto>(
      ApiPathAskGateway.ASTRO_CRM_ASTROLOGERS_OPEN,
      {
        params: {
          sort: 'status_active_livechats_rand',
          per_page: 1,
          ...params,
        },
        headers,
      },
    );

    const [expertDto] = data.astrologers ?? [];

    if (!expertDto) {
      throw new Error('No active astrologers returned for random selection');
    }

    return mapExpertDtoToEntity(expertDto);
  }

  async getExpertReviews(expertId: string, params: GetExpertReviewsParams = {}): Promise<Array<ExpertReview>> {
    const { page, sort, per_page, filter = {} } = params;

    const { data } = await this.askGatewayHttpClient.get<ExpertReviewsDataDto>(
      joinUrls(ApiPathAskGateway.ASTRO_CRM_ASTROLOGERS_OPEN, expertId, 'reviews'),
      {
        params: {
          filter: {
            processing_status: filter.processingStatus,
            astrologer_uuid: filter.expertId,
            customer_source: filter.customerSource,
            customer_source_id: filter.customerSourceId,
            rating: filter.rating,
            'with-text': Number(!!filter.withText),
            'without-text': Number(!!filter.withoutText),
          },
          sort,
          page,
          per_page,
        },
      },
    );

    if (!data.reviews || !Array.isArray(data.reviews)) return [];

    return data.reviews.map((review) => ({
      ...review,
      customer: {
        ...review.customer,
        name: review.customer.name ?? '',
      },
      created_at: convertSecToMs(review.created_at),
    }));
  }

  async getMostSuitableExpert(customerSource: FlowName): Promise<Expert> {
    const expert = await this.getRandomExpert({
      filter: { 'only-trial': 1 },
      customerSource,
    });

    return expert;
  }

  async isExpertOnline(expertId: ExpertId): Promise<boolean> {
    const { data } = await this.askGatewayHttpClient.get<ExpertByIdDataDto>(
      joinUrls(ApiPathAskGateway.ASTRO_CRM_ASTROLOGERS_OPEN, expertId),
    );

    const expert = mapExpertDtoToEntity(data.astrologer);

    return expert.status === ExpertStatus.ONLINE;
  }

  async getAllReviews(params: GetAllReviewsParams): Promise<Array<ExpertReview>> {
    const { perPage, page, minRating, minTextLength, sort } = params;

    const { data } = await this.askGatewayHttpClient.get<ExpertReviewsDataDto>(
      ApiPathAskGateway.ASTRO_CRM_REVIEWS_OPEN,
      {
        params: {
          sort: sort ?? undefined,
          filter: {
            rating: minRating ? getMinimalRatingQueryParam(minRating) : undefined,
            min_text_length: minTextLength,
          },
          per_page: perPage,
          page: page,
        },
      },
    );

    if (!data.reviews || !Array.isArray(data.reviews)) return [];

    return data.reviews.map(mapReviewDtoToEntity);
  }
}
